import { useCallback, useEffect, useMemo, useState } from 'react';
import type { CSSProperties } from 'react';
import { Link } from 'react-router-dom';
import { apiGet, apiPatch, apiPost, TENANT_TOKEN_KEY } from '../../api/client';
import { useAgentSelector } from '../../auth/AgentSelectorContext';

function imageShapeOutlineFilter(color: string, spread = 1): string {
  const shadows: string[] = [];
  for (let y = -spread; y <= spread; y++) {
    for (let x = -spread; x <= spread; x++) {
      if (x === 0 && y === 0) continue;
      if (x * x + y * y <= spread * spread + 0.01) {
        shadows.push(`drop-shadow(${x}px ${y}px 0 ${color})`);
      }
    }
  }
  return shadows.join(' ');
}

/** Igual que en el backend: 32 bytes en base64url (data-site-key). */
function generateWebWidgetSiteKeyClient(): string {
  const bytes = new Uint8Array(32);
  crypto.getRandomValues(bytes);
  let bin = '';
  for (let i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i]);
  const b64 = btoa(bin);
  return b64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

function normalizeWebWidgetScriptOriginClient(raw: string): string | null {
  const t = raw.trim();
  if (!t) return null;
  try {
    let url = t;
    if (!/^https?:\/\//i.test(url)) url = `https://${url}`;
    const u = new URL(url);
    if (!u.hostname) return null;
    return `${u.protocol}//${u.host}`;
  } catch {
    return null;
  }
}

type WhatsappJsRuntime = {
  phase: 'off' | 'starting' | 'qr' | 'ready' | 'error';
  qrDataUrl?: string;
  /** true si el backend ya recibió el string del QR pero aún no pudo armar la imagen */
  hasQrPayload?: boolean;
  loadingPercent?: number | null;
  pairingCode?: string | null;
  linkMode?: 'qr' | 'phone';
  error?: string;
  dbStatus: string;
  externalPageId: string | null;
  displayName: string | null;
};

type WhatsappJsPolicy = {
  inboundMode: 'none' | 'all' | 'allowlist';
  allowlist: string[];
};

type WebWidgetTheme = {
  accentColor: string;
  panelBackground: string;
  messagesBackground: string;
  footerBackground: string;
  sendButtonColor: string;
  headerBackground: string;
  headerTextColor: string;
  visitorBubbleColor: string;
  visitorTextColor: string;
  agentBubbleColor: string;
  agentTextColor: string;
  chatTitle?: string;
  launcherIconUrl?: string;
};

const DEFAULT_WEB_WIDGET_THEME: WebWidgetTheme = {
  accentColor: '#6366f1',
  panelBackground: '#ffffff',
  messagesBackground: '#f8fafc',
  footerBackground: '#ffffff',
  sendButtonColor: '#6366f1',
  headerBackground: '#ffffff',
  headerTextColor: '#0f172a',
  visitorBubbleColor: '#f1f5f9',
  visitorTextColor: '#0f172a',
  agentBubbleColor: '#ffffff',
  agentTextColor: '#0f172a',
};

const WEB_WIDGET_THEME_PRESETS: Array<{ label: string } & WebWidgetTheme> = [
  { label: 'Indigo (default)', ...DEFAULT_WEB_WIDGET_THEME },
  {
    label: 'Violeta marca',
    accentColor: '#7c3aed',
    panelBackground: '#ffffff',
    messagesBackground: '#f5f3ff',
    footerBackground: '#ffffff',
    sendButtonColor: '#7c3aed',
    headerBackground: '#ffffff',
    headerTextColor: '#0f172a',
    visitorBubbleColor: '#f5f3ff',
    visitorTextColor: '#0f172a',
    agentBubbleColor: '#ffffff',
    agentTextColor: '#0f172a',
  },
  {
    label: 'Verde',
    accentColor: '#059669',
    panelBackground: '#ffffff',
    messagesBackground: '#ecfdf5',
    footerBackground: '#ffffff',
    sendButtonColor: '#059669',
    headerBackground: '#ffffff',
    headerTextColor: '#0f172a',
    visitorBubbleColor: '#ecfdf5',
    visitorTextColor: '#0f172a',
    agentBubbleColor: '#ffffff',
    agentTextColor: '#0f172a',
  },
  {
    label: 'Noche',
    accentColor: '#38bdf8',
    panelBackground: '#0f172a',
    messagesBackground: '#1e293b',
    footerBackground: '#0f172a',
    sendButtonColor: '#38bdf8',
    headerBackground: '#0f172a',
    headerTextColor: '#f8fafc',
    visitorBubbleColor: '#334155',
    visitorTextColor: '#f8fafc',
    agentBubbleColor: '#1e293b',
    agentTextColor: '#f8fafc',
  },
  {
    label: 'Coral',
    accentColor: '#f97316',
    panelBackground: '#fff7ed',
    messagesBackground: '#ffedd5',
    footerBackground: '#fff7ed',
    sendButtonColor: '#f97316',
    headerBackground: '#fff7ed',
    headerTextColor: '#0f172a',
    visitorBubbleColor: '#fed7aa',
    visitorTextColor: '#0f172a',
    agentBubbleColor: '#ffffff',
    agentTextColor: '#0f172a',
  },
];

function parseWebWidgetThemeFromRow(settings: Integ['providerSettings'], displayName?: string | null): WebWidgetTheme {
  const ww = settings?.web_widget;
  if (!ww || typeof ww !== 'object') {
    return {
      ...DEFAULT_WEB_WIDGET_THEME,
      ...(displayName?.trim() ? { chatTitle: displayName.trim() } : {}),
    };
  }
  const t = ww as Partial<WebWidgetTheme>;
  const hex = (v: unknown, fallback: string) =>
    typeof v === 'string' && /^#[0-9a-fA-F]{6}$/.test(v.trim()) ? v.trim() : fallback;
  const accentColor = hex(t.accentColor, DEFAULT_WEB_WIDGET_THEME.accentColor);
  const panelBackground = hex(t.panelBackground, DEFAULT_WEB_WIDGET_THEME.panelBackground);
  const messagesBackground = hex(t.messagesBackground, DEFAULT_WEB_WIDGET_THEME.messagesBackground);
  const chatTitleRaw =
    typeof t.chatTitle === 'string' && t.chatTitle.trim()
      ? t.chatTitle.trim().slice(0, 80)
      : displayName?.trim() || undefined;
  return {
    accentColor,
    panelBackground,
    messagesBackground,
    footerBackground: hex(t.footerBackground, panelBackground),
    sendButtonColor: hex(t.sendButtonColor, accentColor),
    headerBackground: hex(t.headerBackground, DEFAULT_WEB_WIDGET_THEME.headerBackground),
    headerTextColor: hex(t.headerTextColor, DEFAULT_WEB_WIDGET_THEME.headerTextColor),
    visitorBubbleColor: hex(t.visitorBubbleColor, DEFAULT_WEB_WIDGET_THEME.visitorBubbleColor),
    visitorTextColor: hex(t.visitorTextColor, DEFAULT_WEB_WIDGET_THEME.visitorTextColor),
    agentBubbleColor: hex(t.agentBubbleColor, DEFAULT_WEB_WIDGET_THEME.agentBubbleColor),
    agentTextColor: hex(t.agentTextColor, DEFAULT_WEB_WIDGET_THEME.agentTextColor),
    ...(chatTitleRaw ? { chatTitle: chatTitleRaw } : {}),
    ...(typeof t.launcherIconUrl === 'string' && t.launcherIconUrl.trim()
      ? { launcherIconUrl: t.launcherIconUrl.trim() }
      : {}),
  };
}

type Integ = {
  id: string;
  provider: string;
  displayName: string | null;
  status: string;
  externalPageId: string | null;
  webWidgetScriptOrigin?: string | null;
  webWidgetTestScriptOrigin?: string | null;
  virtualAgentId: string | null;
  hasPageToken: boolean;
  /** whatsapp_js: { whatsapp_js }; web_widget: { web_widget: theme } */
  providerSettings?: { whatsapp_js?: WhatsappJsPolicy; web_widget?: Partial<WebWidgetTheme> } | null;
};

type Agent = { id: string; name: string };

export type ProviderId =
  | 'facebook'
  | 'instagram'
  | 'whatsapp_js'
  | 'whatsapp_meta'
  | 'telegram'
  | 'email'
  | 'sms_twilio'
  | 'slack'
  | 'teams'
  | 'web_widget';

type ChannelModalState =
  | { kind: 'new'; provider: ProviderId }
  | { kind: 'edit'; integrationId: string };

const TABLE_PAGE_SIZE = 10;

const CHANNEL_ORDER: ProviderId[] = [
  'facebook',
  'whatsapp_js',
  'whatsapp_meta',
  'instagram',
  'telegram',
  'web_widget',
  'slack',
  'sms_twilio',
  'email',
  'teams',
];

function providerTableLabel(p: string): string {
  const m: Record<string, string> = {
    facebook: 'Facebook · Messenger',
    instagram: 'Instagram · Direct',
    whatsapp_js: 'WhatsApp · QR (web.js)',
    whatsapp_meta: 'WhatsApp · Meta Cloud',
    telegram: 'Telegram · Bot',
    email: 'Email',
    sms_twilio: 'SMS · Twilio',
    slack: 'Slack',
    teams: 'Microsoft Teams',
    web_widget: 'Chat web',
  };
  return m[p] ?? p;
}

const webhookPath = '/webhooks/messenger';

function LogoFacebook() {
  return (
    <svg width="44" height="44" viewBox="0 0 24 24" aria-hidden style={{ flexShrink: 0 }}>
      <path
        fill="#1877F2"
        d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"
      />
    </svg>
  );
}

function LogoWhatsapp() {
  return (
    <svg width="44" height="44" viewBox="0 0 24 24" aria-hidden style={{ flexShrink: 0 }}>
      <path
        fill="#25D366"
        d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"
      />
    </svg>
  );
}

function LogoInstagram() {
  return (
    <svg width="44" height="44" viewBox="0 0 24 24" aria-hidden style={{ flexShrink: 0 }}>
      <defs>
        <radialGradient id="igGrad" cx="30%" cy="107%" r="150%">
          <stop offset="0%" stopColor="#fdf497" />
          <stop offset="45%" stopColor="#fd5949" />
          <stop offset="70%" stopColor="#d6249f" />
          <stop offset="100%" stopColor="#285AEB" />
        </radialGradient>
      </defs>
      <rect x="2" y="2" width="20" height="20" rx="5" fill="url(#igGrad)" />
      <circle cx="12" cy="12" r="4.2" fill="none" stroke="#fff" strokeWidth="1.4" />
      <circle cx="17.4" cy="6.6" r="1.15" fill="#fff" />
    </svg>
  );
}

function LogoTelegram() {
  return (
    <svg width="44" height="44" viewBox="0 0 24 24" aria-hidden style={{ flexShrink: 0 }}>
      <circle cx="12" cy="12" r="12" fill="#229ED9" />
      <path
        fill="#fff"
        d="M17.2 7.3L5.8 12.1c-.7.3-.7.7-.1.9l2.9.9 6.7-4.2c.4-.2.7-.1.4.1l-5.4 4.9-.2 3c.2 0 .3-.1.5-.2l2.4-2.3 5 3.7c.9.5 1.6.2 1.8-.7l1.8-8.5c.3-1.2-.5-1.7-1.3-1.4z"
      />
    </svg>
  );
}

function LogoEmail() {
  return (
    <svg width="44" height="44" viewBox="0 0 24 24" aria-hidden style={{ flexShrink: 0 }}>
      <rect x="2" y="4" width="20" height="16" rx="2" fill="#7c3aed" />
      <path fill="#ede9fe" d="M2 6l10 7 10-7v-2H2v2z" />
    </svg>
  );
}

function LogoSms() {
  return (
    <svg width="44" height="44" viewBox="0 0 24 24" aria-hidden style={{ flexShrink: 0 }}>
      <rect x="3" y="5" width="18" height="14" rx="2" fill="#F22F46" />
      <path fill="#fff" d="M7 9h10v2H7V9zm0 3h7v2H7v-2z" />
    </svg>
  );
}

function LogoSlack() {
  return (
    <svg width="44" height="44" viewBox="0 0 24 24" aria-hidden style={{ flexShrink: 0 }}>
      <path fill="#E01E5A" d="M5.2 15.2a2.4 2.4 0 0 1-2.4 2.4 2.4 2.4 0 1 1 2.4-2.4zM8 15.2h2.4v2.4a2.4 2.4 0 1 1-2.4-2.4V15.2z" />
      <path fill="#36C5F0" d="M8.8 5.2a2.4 2.4 0 1 1 4.8 0v2.4H11.2a2.4 2.4 0 1 1-2.4-2.4V5.2z" />
      <path fill="#2EB67D" d="M18.8 8.8a2.4 2.4 0 0 1 2.4 2.4 2.4 2.4 0 1 1-2.4-2.4H18.8zm-2.4 4.8V11.2h2.4a2.4 2.4 0 1 1-2.4 2.4z" />
      <path fill="#ECB22E" d="M15.2 18.8a2.4 2.4 0 1 1-4.8 0v-2.4h2.4a2.4 2.4 0 1 1 2.4 2.4v0z" />
    </svg>
  );
}

function LogoTeams() {
  return (
    <svg width="44" height="44" viewBox="0 0 24 24" aria-hidden style={{ flexShrink: 0 }}>
      <rect x="2" y="3" width="9" height="9" rx="1" fill="#5059C9" />
      <rect x="13" y="3" width="9" height="9" rx="1" fill="#7B83EB" />
      <rect x="2" y="14" width="9" height="7" rx="1" fill="#5059C9" />
      <rect x="13" y="14" width="9" height="7" rx="1" fill="#7B83EB" />
    </svg>
  );
}

function LogoWidget() {
  return (
    <svg width="44" height="44" viewBox="0 0 24 24" aria-hidden style={{ flexShrink: 0 }}>
      <rect x="3" y="6" width="18" height="12" rx="2" fill="#0ea5e9" />
      <circle cx="9" cy="12" r="2" fill="#fff" />
      <circle cx="15" cy="12" r="2" fill="#fff" />
    </svg>
  );
}

function carouselCardStyle(_accent: string): CSSProperties {
  return {
    flex: '0 0 260px',
    scrollSnapAlign: 'start',
    borderRadius: 16,
    border: '1px solid #e2e8f0',
    background: '#fff',
    padding: 16,
    cursor: 'pointer',
    textAlign: 'left',
    boxShadow: '0 2px 8px rgba(15, 23, 42, 0.06)',
    display: 'flex',
    flexDirection: 'column',
    gap: 10,
    transition: 'box-shadow 0.15s ease',
  };
}

function WebChatEmbedSnippet({
  integrationId,
  siteKey,
  embedBaseUrl,
  previewOnly,
  variant = 'production',
  theme = DEFAULT_WEB_WIDGET_THEME,
  chatTitle = 'Chat',
}: {
  integrationId: string;
  siteKey: string;
  /** Origen HTTPS del backend (sin barra final), ej. https://api.empresa.com */
  embedBaseUrl: string;
  /** Si aún no está conectado en el servidor */
  previewOnly?: boolean;
  variant?: 'production' | 'test';
  theme?: WebWidgetTheme;
  chatTitle?: string;
}) {
  const [copied, setCopied] = useState(false);
  const base = embedBaseUrl.replace(/\/$/, '');
  const escKey = siteKey.replace(/&/g, '&amp;').replace(/"/g, '&quot;');
  const escTitle = chatTitle.replace(/&/g, '&amp;').replace(/"/g, '&quot;');
  const launcherAttr = theme.launcherIconUrl?.trim()
    ? ` data-launcher-icon="${theme.launcherIconUrl.trim().replace(/&/g, '&amp;').replace(/"/g, '&quot;')}"`
    : '';
  const snippet = `<script src="${base}/widget/embed.js" data-integration="${integrationId}" data-site-key="${escKey}" data-chat-title="${escTitle}" data-accent="${theme.accentColor}" data-panel-bg="${theme.panelBackground}" data-messages-bg="${theme.messagesBackground}" data-footer-bg="${theme.footerBackground}" data-send-btn="${theme.sendButtonColor}" data-header-bg="${theme.headerBackground}" data-header-text="${theme.headerTextColor}" data-visitor-bubble="${theme.visitorBubbleColor}" data-visitor-text="${theme.visitorTextColor}" data-agent-bubble="${theme.agentBubbleColor}" data-agent-text="${theme.agentTextColor}"${launcherAttr}></script>`;
  const isTest = variant === 'test';

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(snippet);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      window.prompt('Copiá el código:', snippet);
    }
  };

  return (
    <div
      style={{
        marginTop: 20,
        padding: 14,
        background: isTest ? '#fffbeb' : '#ecfeff',
        borderRadius: 10,
        border: isTest ? '1px solid #fcd34d' : '1px solid #a5f3fc',
      }}
    >
      <strong style={{ color: isTest ? '#92400e' : '#0c4a6e' }}>
        {isTest ? 'HTML de prueba (URL de test)' : 'HTML para incrustar (producción)'}
      </strong>
      <p style={{ margin: '8px 0', fontSize: 13, color: '#334155', lineHeight: 1.5 }}>
        Pegá este bloque antes de <code style={{ background: '#cffafe', padding: '2px 6px', borderRadius: 4 }}>&lt;/body&gt;</code>.
        Los colores guardados en esta pantalla se aplican solos en sitios ya embebidos (el script consulta la API al cargar).
        Los atributos <code>data-*</code> de colores son opcionales
        (solo si querés forzar valores distintos a los de consola).
      </p>
      {previewOnly ? (
        <p style={{ margin: '0 0 10px', fontSize: 12, color: '#b45309', lineHeight: 1.45 }}>
          Vista previa: el widget solo responderá después de <strong>guardar</strong> la clave y el dominio y marcar el
          canal como <strong>Conectado</strong> con un agente.
        </p>
      ) : null}
      <textarea
        readOnly
        rows={4}
        value={snippet}
        onFocus={(e) => e.target.select()}
        style={{
          width: '100%',
          boxSizing: 'border-box',
          fontFamily: 'ui-monospace, monospace',
          fontSize: 12,
          padding: 10,
          borderRadius: 8,
          border: '1px solid #67e8f9',
          background: '#fff',
          resize: 'vertical',
        }}
      />
      <button
        type="button"
        onClick={() => void copy()}
        style={{
          marginTop: 10,
          padding: '10px 16px',
          borderRadius: 8,
          border: 'none',
          background: isTest ? '#d97706' : '#0ea5e9',
          color: '#fff',
          fontWeight: 600,
          cursor: 'pointer',
        }}
      >
        {copied ? 'Copiado' : 'Copiar código'}
      </button>
    </div>
  );
}

function WebChatIntegrationForm({
  row,
  agents,
  token,
  onSaved,
  setErr,
  setSaved,
}: {
  row: Integ;
  agents: Agent[];
  token: string;
  onSaved: () => void;
  setErr: (s: string) => void;
  setSaved: (s: string) => void;
}) {
  const [scriptOrigin, setScriptOrigin] = useState(row.webWidgetScriptOrigin ?? '');
  const [testScriptOrigin, setTestScriptOrigin] = useState(row.webWidgetTestScriptOrigin ?? '');
  const [siteKeyDraft, setSiteKeyDraft] = useState(row.externalPageId ?? '');
  const [agentId, setAgentId] = useState(row.virtualAgentId ?? '');
  const [accessTok, setAccessTok] = useState('');
  const [status, setStatus] = useState(row.status);
  const [busy, setBusy] = useState(false);
  const [launcherUploadBusy, setLauncherUploadBusy] = useState(false);
  const [widgetTheme, setWidgetTheme] = useState<WebWidgetTheme>(() =>
    parseWebWidgetThemeFromRow(row.providerSettings, row.displayName)
  );

  useEffect(() => {
    setScriptOrigin(row.webWidgetScriptOrigin ?? '');
    setTestScriptOrigin(row.webWidgetTestScriptOrigin ?? '');
    setSiteKeyDraft(row.externalPageId ?? '');
    setAgentId(row.virtualAgentId ?? '');
    setStatus(row.status);
    setAccessTok('');
    setWidgetTheme(parseWebWidgetThemeFromRow(row.providerSettings, row.displayName));
  }, [
    row.id,
    row.externalPageId,
    row.webWidgetScriptOrigin,
    row.webWidgetTestScriptOrigin,
    row.virtualAgentId,
    row.status,
    row.providerSettings,
  ]);

  const normalizedOrigin = useMemo(() => normalizeWebWidgetScriptOriginClient(scriptOrigin), [scriptOrigin]);
  const normalizedTestOrigin = useMemo(
    () => normalizeWebWidgetScriptOriginClient(testScriptOrigin),
    [testScriptOrigin]
  );
  const canGenerateKey = Boolean(normalizedOrigin || normalizedTestOrigin);
  const snippetReadyProd = Boolean(normalizedOrigin && siteKeyDraft.trim());
  const snippetReadyTest = Boolean(normalizedTestOrigin && siteKeyDraft.trim());

  const inpStyle: CSSProperties = {
    padding: '10px 12px',
    borderRadius: 8,
    border: '1px solid #cbd5e1',
    fontSize: 14,
  };

  const copyKey = async () => {
    const k = siteKeyDraft.trim();
    if (!k) return;
    try {
      await navigator.clipboard.writeText(k);
      setSaved('Clave copiada.');
      window.setTimeout(() => setSaved(''), 2000);
    } catch {
      window.prompt('Copiá la clave:', k);
    }
  };

  const generateKey = () => {
    if (!canGenerateKey) return;
    setSiteKeyDraft(generateWebWidgetSiteKeyClient());
  };

  const uploadLauncherIcon = async (file: File | null) => {
    if (!file || !token) return;
    if (file.type !== 'image/png') {
      setErr('Solo se admite PNG para el botón flotante.');
      return;
    }
    if (!siteKeyDraft.trim()) {
      setErr('Generá la clave del sitio antes de subir el ícono.');
      return;
    }
    setErr('');
    setLauncherUploadBusy(true);
    try {
      const form = new FormData();
      form.append('file', file);
      const r = await fetch(`/api/console/integrations/${row.id}/web-widget-launcher-icon`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: form,
      });
      const j = (await r.json()) as {
        success?: boolean;
        data?: { launcherIconUrl?: string };
        error?: { message?: string };
      };
      if (!r.ok || !j.success) {
        throw new Error(j.error?.message ?? `HTTP ${r.status}`);
      }
      const url = j.data?.launcherIconUrl;
      if (url) {
        setWidgetTheme((t) => ({ ...t, launcherIconUrl: url }));
      }
      setSaved('Ícono del botón subido. Guardá los cambios si editaste otros campos.');
    } catch (ex) {
      setErr((ex as Error).message);
    } finally {
      setLauncherUploadBusy(false);
    }
  };

  const removeLauncherIcon = async () => {
    if (!token) return;
    setErr('');
    setLauncherUploadBusy(true);
    try {
      const r = await fetch(`/api/console/integrations/${row.id}/web-widget-launcher-icon`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      const j = (await r.json()) as { success?: boolean; error?: { message?: string } };
      if (!r.ok || !j.success) {
        throw new Error(j.error?.message ?? `HTTP ${r.status}`);
      }
      setWidgetTheme((t) => {
        const next = { ...t };
        delete next.launcherIconUrl;
        return next;
      });
      setSaved('Ícono del botón eliminado.');
    } catch (ex) {
      setErr((ex as Error).message);
    } finally {
      setLauncherUploadBusy(false);
    }
  };

  const save = async (e: React.FormEvent) => {
    e.preventDefault();
    setErr('');
    setSaved('');

    const normDom = scriptOrigin.trim() ? normalizeWebWidgetScriptOriginClient(scriptOrigin) : null;
    if (scriptOrigin.trim() && !normDom) {
      setErr('Dominio público inválido. Ejemplo: https://api.tudominio.com o api.tudominio.com');
      return;
    }

    const normTestDom = testScriptOrigin.trim() ? normalizeWebWidgetScriptOriginClient(testScriptOrigin) : null;
    if (testScriptOrigin.trim() && !normTestDom) {
      setErr('URL de test inválida. Ejemplo: http://localhost:3010 o https://staging-api.tudominio.com');
      return;
    }

    if (status === 'connected') {
      if (!siteKeyDraft.trim()) {
        setErr('Generá la clave del sitio (después de indicar el dominio) antes de conectar.');
        return;
      }
      if (!normDom) {
        setErr('Indicá el dominio público del backend antes de conectar.');
        return;
      }
      if (!agentId.trim()) {
        setErr('Elegí un agente virtual antes de conectar.');
        return;
      }
    }

    setBusy(true);
    try {
      const body: Record<string, unknown> = {
        virtualAgentId: agentId.trim() || null,
        status,
        externalPageId: siteKeyDraft.trim() || null,
        webWidgetScriptOrigin: normDom,
        webWidgetTestScriptOrigin: normTestDom,
        webWidgetTheme: widgetTheme,
      };
      if (accessTok.trim()) body.accessToken = accessTok.trim();
      await apiPatch(`/api/console/integrations/${row.id}`, body, token, { successMessage: 'Cambios guardados' });
      setSaved('Guardado.');
      setAccessTok('');
      onSaved();
    } catch (ex) {
      setErr((ex as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <form onSubmit={(e) => void save(e)}>
        <div style={{ display: 'grid', gap: 12 }}>
          <label
            style={{
              display: 'flex',
              flexDirection: 'column',
              fontSize: 13,
              fontWeight: 600,
              color: '#475569',
              gap: 4,
            }}
          >
            Dominio público del backend (HTTPS)
            <input
              style={inpStyle}
              value={scriptOrigin}
              onChange={(e) => setScriptOrigin(e.target.value)}
              placeholder="https://api.tudominio.com o api.tudominio.com"
              autoComplete="off"
            />
            <span style={{ fontWeight: 400, fontSize: 11, color: '#94a3b8' }}>
              Es la base URL donde está el backend (sin path). Se usa en el <code>src</code> del script y se guarda al
              pulsar Guardar.
            </span>
          </label>

          <label
            style={{
              display: 'flex',
              flexDirection: 'column',
              fontSize: 13,
              fontWeight: 600,
              color: '#475569',
              gap: 4,
            }}
          >
            URL de test (opcional)
            <input
              style={inpStyle}
              value={testScriptOrigin}
              onChange={(e) => setTestScriptOrigin(e.target.value)}
              placeholder="http://localhost:3010 o https://staging-api.tudominio.com"
              autoComplete="off"
            />
            <span style={{ fontWeight: 400, fontSize: 11, color: '#94a3b8' }}>
              Para desarrollo o staging: permite generar la clave y ver un segundo snippet apuntando a este origen. No
              reemplaza el dominio de producción al conectar el canal.
            </span>
          </label>

          <div>
            <button
              type="button"
              disabled={!canGenerateKey}
              onClick={generateKey}
              style={{
                padding: '10px 16px',
                borderRadius: 8,
                border: 'none',
                background: canGenerateKey ? '#0369a1' : '#94a3b8',
                color: '#fff',
                fontWeight: 600,
                cursor: canGenerateKey ? 'pointer' : 'not-allowed',
              }}
            >
              {siteKeyDraft.trim() ? 'Generar otra clave (solo en el navegador hasta que guardes)' : 'Generar clave del sitio'}
            </button>
            {!canGenerateKey ? (
              <p style={{ margin: '8px 0 0', fontSize: 12, color: '#b45309' }}>
                Completá el dominio de producción o la URL de test (al menos uno) para habilitar la generación de la clave.
              </p>
            ) : null}
          </div>

          <div
            style={{
              padding: 12,
              background: '#f8fafc',
              borderRadius: 8,
              border: '1px solid #e2e8f0',
            }}
          >
            <div style={{ fontSize: 13, fontWeight: 600, color: '#475569', marginBottom: 8 }}>
              Clave del sitio (<code style={{ fontSize: 12 }}>data-site-key</code>) — se guarda al pulsar Guardar
            </div>
            <div
              style={{
                fontFamily: 'ui-monospace, monospace',
                fontSize: 12,
                wordBreak: 'break-all',
                color: '#0f172a',
                lineHeight: 1.5,
                minHeight: 20,
              }}
            >
              {siteKeyDraft.trim() || '— Todavía no generaste clave —'}
            </div>
            {siteKeyDraft.trim() ? (
              <button
                type="button"
                onClick={() => void copyKey()}
                style={{
                  marginTop: 10,
                  padding: '8px 12px',
                  borderRadius: 8,
                  border: '1px solid #cbd5e1',
                  background: '#fff',
                  fontWeight: 600,
                  fontSize: 12,
                  cursor: 'pointer',
                  color: '#334155',
                }}
              >
                Copiar clave
              </button>
            ) : null}
          </div>

          <div
            style={{
              padding: 14,
              background: '#f8fafc',
              borderRadius: 10,
              border: '1px solid #e2e8f0',
            }}
          >
            <div style={{ fontSize: 13, fontWeight: 600, color: '#475569', marginBottom: 10 }}>
              Apariencia del chat flotante
            </div>
            <p style={{ margin: '0 0 12px', fontSize: 12, color: '#64748b', lineHeight: 1.5 }}>
              Guardá los cambios y recargá la página donde está el chat: los sitios embebidos leen colores e ícono del
              botón desde el servidor (no hace falta volver a pegar el HTML).
            </p>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 12 }}>
              {WEB_WIDGET_THEME_PRESETS.map((preset) => (
                <button
                  key={preset.label}
                  type="button"
                  onClick={() =>
                    setWidgetTheme({
                      accentColor: preset.accentColor,
                      panelBackground: preset.panelBackground,
                      messagesBackground: preset.messagesBackground,
                      footerBackground: preset.footerBackground,
                      sendButtonColor: preset.sendButtonColor,
                      headerBackground: preset.headerBackground,
                      headerTextColor: preset.headerTextColor,
                      visitorBubbleColor: preset.visitorBubbleColor,
                      visitorTextColor: preset.visitorTextColor,
                      agentBubbleColor: preset.agentBubbleColor,
                      agentTextColor: preset.agentTextColor,
                      chatTitle: widgetTheme.chatTitle,
                      launcherIconUrl: widgetTheme.launcherIconUrl,
                    })
                  }
                  style={{
                    padding: '6px 10px',
                    borderRadius: 999,
                    border: '1px solid #cbd5e1',
                    background: '#fff',
                    fontSize: 12,
                    fontWeight: 600,
                    cursor: 'pointer',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 6,
                  }}
                >
                  <span
                    aria-hidden
                    style={{
                      width: 14,
                      height: 14,
                      borderRadius: '50%',
                      background: preset.accentColor,
                      border: '1px solid rgba(15,23,42,0.12)',
                    }}
                  />
                  {preset.label}
                </button>
              ))}
            </div>
            <label
              style={{
                display: 'flex',
                flexDirection: 'column',
                fontSize: 12,
                fontWeight: 600,
                color: '#475569',
                gap: 6,
                marginBottom: 12,
              }}
            >
              Título del chat (cabecera)
              <input
                style={{ ...inpStyle, fontSize: 13 }}
                value={widgetTheme.chatTitle ?? ''}
                onChange={(e) =>
                  setWidgetTheme((t) => ({
                    ...t,
                    chatTitle: e.target.value.slice(0, 80),
                  }))
                }
              placeholder="Ej. Soporte, Ventas, Chat"
              maxLength={80}
            />
          </label>
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 8,
              marginBottom: 12,
              padding: 12,
              borderRadius: 10,
              border: '1px solid #e2e8f0',
              background: '#fff',
            }}
          >
            <span style={{ fontSize: 12, fontWeight: 600, color: '#475569' }}>
              Ícono del botón flotante (PNG)
            </span>
            <p style={{ margin: 0, fontSize: 12, color: '#64748b', lineHeight: 1.45 }}>
              Imagen cuadrada recomendada (56×56 px o más). Se aplica al recargar el sitio embebido.
            </p>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
              {widgetTheme.launcherIconUrl ? (
                <span
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 56,
                    height: 56,
                    padding: 4,
                    boxSizing: 'border-box',
                  }}
                >
                  <img
                    src={widgetTheme.launcherIconUrl}
                    alt=""
                    width={48}
                    height={48}
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'contain',
                      filter: imageShapeOutlineFilter(widgetTheme.accentColor),
                    }}
                  />
                </span>
              ) : (
                <div
                  style={{
                    width: 56,
                    height: 56,
                    borderRadius: 12,
                    border: '1px dashed #cbd5e1',
                    background: '#f8fafc',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: 10,
                    color: '#94a3b8',
                    textAlign: 'center',
                    padding: 4,
                  }}
                >
                  Sin ícono
                </div>
              )}
              <label
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 8,
                  padding: '8px 12px',
                  borderRadius: 8,
                  border: '1px solid #cbd5e1',
                  background: '#fff',
                  fontSize: 13,
                  fontWeight: 600,
                  color: '#334155',
                  cursor: launcherUploadBusy ? 'wait' : 'pointer',
                }}
              >
                <input
                  type="file"
                  accept="image/png"
                  disabled={launcherUploadBusy || !siteKeyDraft.trim()}
                  style={{ display: 'none' }}
                  onChange={(e) => {
                    const f = e.target.files?.[0] ?? null;
                    e.target.value = '';
                    void uploadLauncherIcon(f);
                  }}
                />
                {launcherUploadBusy ? 'Subiendo…' : 'Subir PNG'}
              </label>
              {widgetTheme.launcherIconUrl ? (
                <button
                  type="button"
                  disabled={launcherUploadBusy}
                  onClick={() => void removeLauncherIcon()}
                  style={{
                    padding: '8px 12px',
                    borderRadius: 8,
                    border: '1px solid #fecaca',
                    background: '#fff',
                    fontSize: 13,
                    fontWeight: 600,
                    color: '#b91c1c',
                    cursor: 'pointer',
                  }}
                >
                  Quitar
                </button>
              ) : null}
            </div>
            <label
              style={{
                display: 'flex',
                flexDirection: 'column',
                fontSize: 12,
                fontWeight: 600,
                color: '#475569',
                gap: 6,
              }}
            >
              O URL pública del PNG
              <input
                style={{ ...inpStyle, fontSize: 13 }}
                value={widgetTheme.launcherIconUrl ?? ''}
                onChange={(e) =>
                  setWidgetTheme((t) => ({
                    ...t,
                    launcherIconUrl: e.target.value.slice(0, 2048) || undefined,
                  }))
                }
                placeholder="https://tu-dominio.com/icono-chat.png"
              />
            </label>
            {!siteKeyDraft.trim() ? (
              <p style={{ margin: 0, fontSize: 11, color: '#b45309' }}>
                Generá la clave del sitio para habilitar la subida de PNG.
              </p>
            ) : null}
          </div>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
                gap: 12,
              }}
            >
              {(
                [
                  ['accentColor', 'Color de acento (borde header + launcher)'],
                  ['headerBackground', 'Fondo del header'],
                  ['headerTextColor', 'Texto del header'],
                  ['panelBackground', 'Fondo del panel'],
                  ['messagesBackground', 'Fondo del área de mensajes'],
                  ['footerBackground', 'Fondo del footer (input)'],
                  ['sendButtonColor', 'Botón Enviar'],
                  ['visitorBubbleColor', 'Burbuja visitante (fondo)'],
                  ['visitorTextColor', 'Burbuja visitante (texto)'],
                  ['agentBubbleColor', 'Burbuja agente (fondo)'],
                  ['agentTextColor', 'Burbuja agente (texto)'],
                ] as const
              ).map(([key, label]) => (
                <label
                  key={key}
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    fontSize: 12,
                    fontWeight: 600,
                    color: '#475569',
                    gap: 6,
                  }}
                >
                  {label}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <input
                      type="color"
                      value={widgetTheme[key]}
                      onChange={(e) => setWidgetTheme((t) => ({ ...t, [key]: e.target.value }))}
                      style={{ width: 44, height: 36, padding: 0, border: '1px solid #cbd5e1', borderRadius: 8 }}
                    />
                    <input
                      style={{ ...inpStyle, flex: 1, fontFamily: 'ui-monospace, monospace', fontSize: 12 }}
                      value={widgetTheme[key]}
                      onChange={(e) => {
                        const v = e.target.value.trim();
                        if (/^#[0-9a-fA-F]{0,6}$/.test(v)) {
                          setWidgetTheme((t) => ({ ...t, [key]: v }));
                        }
                      }}
                      spellCheck={false}
                    />
                  </div>
                </label>
              ))}
            </div>
            <div
              style={{
                marginTop: 12,
                padding: 10,
                borderRadius: 10,
                border: '1px solid #e2e8f0',
                background: widgetTheme.panelBackground,
                maxWidth: 220,
                marginLeft: 'auto',
              }}
            >
              <div
                style={{
                  background: widgetTheme.headerBackground,
                  color: widgetTheme.headerTextColor,
                  fontSize: 12,
                  fontWeight: 700,
                  padding: '8px 10px',
                  borderRadius: '10px 10px 0 0',
                  borderBottom: '3px solid ' + widgetTheme.accentColor,
                }}
              >
                {widgetTheme.chatTitle?.trim() || 'Chat'}
              </div>
              <div
                style={{
                  background: widgetTheme.messagesBackground,
                  padding: 10,
                  fontSize: 12,
                  minHeight: 60,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 6,
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'flex-start' }}>
                  <div
                    style={{
                      background: widgetTheme.agentBubbleColor,
                      color: widgetTheme.agentTextColor,
                      border: '1px solid #e2e8f0',
                      borderRadius: 10,
                      borderBottomLeftRadius: 3,
                      padding: '6px 8px',
                      maxWidth: '85%',
                    }}
                  >
                    Hola, ¿en qué te ayudo?
                  </div>
                </div>
                <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <div
                    style={{
                      background: widgetTheme.visitorBubbleColor,
                      color: widgetTheme.visitorTextColor,
                      borderRadius: 10,
                      borderBottomRightRadius: 3,
                      padding: '6px 8px',
                      maxWidth: '85%',
                    }}
                  >
                    Quiero info
                  </div>
                </div>
              </div>
              <div
                style={{
                  display: 'flex',
                  gap: 6,
                  padding: 8,
                  background: widgetTheme.footerBackground,
                  borderTop: '1px solid #e2e8f0',
                }}
              >
                <div
                  style={{
                    flex: 1,
                    height: 28,
                    borderRadius: 8,
                    background: '#fff',
                    border: '1px solid #cbd5e1',
                  }}
                />
                <div
                  style={{
                    padding: '4px 10px',
                    borderRadius: 8,
                    background: widgetTheme.sendButtonColor,
                    color: '#fff',
                    fontSize: 11,
                    fontWeight: 600,
                    display: 'flex',
                    alignItems: 'center',
                  }}
                >
                  Enviar
                </div>
              </div>
            </div>
          </div>

          <label
            style={{
              display: 'flex',
              flexDirection: 'column',
              fontSize: 13,
              fontWeight: 600,
              color: '#475569',
              gap: 4,
            }}
          >
            Agente virtual
            <select style={inpStyle} value={agentId} onChange={(e) => setAgentId(e.target.value)}>
              <option value="">— Elegir —</option>
              {agents.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.name}
                </option>
              ))}
            </select>
          </label>
          <div>
            <label
              style={{
                display: 'flex',
                flexDirection: 'column',
                fontSize: 13,
                fontWeight: 600,
                color: '#475569',
                gap: 4,
              }}
            >
              <span>
                Opcional (reservado)
                {row.hasPageToken ? (
                  <span style={{ fontWeight: 400, color: '#64748b' }}> · hay token guardado</span>
                ) : null}
              </span>
              <input
                style={inpStyle}
                type="password"
                autoComplete="off"
                value={accessTok}
                onChange={(e) => setAccessTok(e.target.value)}
                placeholder={row.hasPageToken ? 'Vacío = no cambiar' : 'No requerido para el chat público'}
              />
            </label>
            <span style={{ fontWeight: 400, fontSize: 11, color: '#94a3b8' }}>
              No hace falta para el snippet; podés dejarlo vacío.
            </span>
          </div>
          <label
            style={{
              display: 'flex',
              flexDirection: 'column',
              fontSize: 13,
              fontWeight: 600,
              color: '#475569',
              gap: 4,
            }}
          >
            Estado
            <select style={inpStyle} value={status} onChange={(e) => setStatus(e.target.value)}>
              <option value="disconnected">Desconectado</option>
              <option value="connected">Conectado</option>
            </select>
          </label>
          <button
            type="submit"
            disabled={busy}
            style={{
              padding: '10px 16px',
              borderRadius: 8,
              border: 'none',
              background: '#0ea5e9',
              color: '#fff',
              fontWeight: 600,
              cursor: busy ? 'default' : 'pointer',
            }}
          >
            {busy ? 'Guardando…' : 'Guardar chat web'}
          </button>
        </div>
      </form>

      {snippetReadyProd ? (
        <WebChatEmbedSnippet
          integrationId={row.id}
          siteKey={siteKeyDraft.trim()}
          embedBaseUrl={normalizedOrigin!}
          previewOnly={status !== 'connected' || !agentId.trim()}
          variant="production"
          theme={widgetTheme}
          chatTitle={widgetTheme.chatTitle?.trim() || row.displayName?.trim() || 'Chat'}
        />
      ) : null}
      {snippetReadyTest ? (
        <WebChatEmbedSnippet
          integrationId={row.id}
          siteKey={siteKeyDraft.trim()}
          embedBaseUrl={normalizedTestOrigin!}
          previewOnly={status !== 'connected' || !agentId.trim()}
          variant="test"
          theme={widgetTheme}
          chatTitle={widgetTheme.chatTitle?.trim() || row.displayName?.trim() || 'Chat'}
        />
      ) : null}
      {!snippetReadyProd && !snippetReadyTest ? (
        <p style={{ marginTop: 16, fontSize: 13, color: '#64748b', lineHeight: 1.5 }}>
          Completá el dominio de producción o la URL de test, generá la clave y guardá para ver el HTML de incrustación.
        </p>
      ) : null}
    </>
  );
}

export function IntegrationsPage() {
  const token = localStorage.getItem(TENANT_TOKEN_KEY)!;
  const { selectedAgentId, selectedAgent } = useAgentSelector();
  const [rows, setRows] = useState<Integ[]>([]);
  const [agents, setAgents] = useState<Agent[]>([]);
  const [err, setErr] = useState('');
  const [saved, setSaved] = useState('');
  const [channelModal, setChannelModal] = useState<ChannelModalState | null>(null);
  const [newIntegrationLabel, setNewIntegrationLabel] = useState('');
  const [creating, setCreating] = useState(false);
  const [tablePage, setTablePage] = useState(1);
  const [tableBusyId, _setTableBusyId] = useState<string | null>(null);
  const [catalogQuery, setCatalogQuery] = useState('');
  const [accountQuery, setAccountQuery] = useState('');

  const publicApiBase = import.meta.env.VITE_PUBLIC_AGENT_API_URL?.replace(/\/$/, '') || '';

  const load = useCallback(async () => {
    try {
      const [data, ag] = await Promise.all([
        apiGet<Integ[]>(`/api/console/integrations${selectedAgentId ? `?agentId=${selectedAgentId}` : ''}`, token),
        apiGet<Agent[]>('/api/console/agents', token),
      ]);
      setRows(data);
      setAgents(ag);
    } catch (e) {
      setErr((e as Error).message);
    }
  }, [token, selectedAgentId]);

  useEffect(() => {
    void load();
  }, [load]);

  const sortedRows = useMemo(
    () =>
      [...rows].sort((a, b) => {
        const p = a.provider.localeCompare(b.provider);
        if (p !== 0) return p;
        return (a.displayName ?? '').localeCompare(b.displayName ?? '');
      }),
    [rows]
  );

  const accountFilteredRows = useMemo(() => {
    const q = accountQuery.trim().toLowerCase();
    if (!q) return sortedRows;
    return sortedRows.filter((row) => {
      const agName = agents.find((a) => a.id === row.virtualAgentId)?.name ?? '';
      const hay = [
        providerTableLabel(row.provider),
        row.provider,
        row.displayName ?? '',
        row.status,
        row.externalPageId ?? '',
        row.webWidgetScriptOrigin ?? '',
        row.webWidgetTestScriptOrigin ?? '',
        row.id,
        agName,
      ]
        .join(' ')
        .toLowerCase();
      return hay.includes(q);
    });
  }, [sortedRows, accountQuery, agents]);

  const tableTotalPages = Math.max(1, Math.ceil(accountFilteredRows.length / TABLE_PAGE_SIZE));
  const tableSlice = useMemo(() => {
    const start = (tablePage - 1) * TABLE_PAGE_SIZE;
    return accountFilteredRows.slice(start, start + TABLE_PAGE_SIZE);
  }, [accountFilteredRows, tablePage]);

  useEffect(() => {
    if (tablePage > tableTotalPages) setTablePage(tableTotalPages);
  }, [tablePage, tableTotalPages]);

  useEffect(() => {
    setTablePage(1);
  }, [accountQuery]);

  useEffect(() => {
    if (channelModal?.kind === 'edit' && !rows.some((r) => r.id === channelModal.integrationId)) {
      setChannelModal(null);
    }
  }, [channelModal, rows]);

  const createIntegration = async (provider: ProviderId) => {
    setErr('');
    setSaved('');
    if (!selectedAgentId) {
      setErr('Seleccioná un agente en el selector superior antes de crear la integración.');
      return;
    }
    setCreating(true);
    try {
      const label = newIntegrationLabel.trim();
      const data = await apiPost<Integ>(
        '/api/console/integrations',
        {
          provider,
          displayName: label || undefined,
          virtualAgentId: selectedAgentId,
        },
        token,
        { successMessage: 'Integración creada' }
      );
      setSaved(
        `Integración creada${selectedAgent ? ` para «${selectedAgent.name}»` : ''}. Completá la configuración abajo.`
      );
      setNewIntegrationLabel('');
      // Evita que el modal se cierre si el reload aún no trae la fila.
      setRows((prev) => (prev.some((r) => r.id === data.id) ? prev : [data, ...prev]));
      setChannelModal({ kind: 'edit', integrationId: data.id });
      void load();
    } catch (ex) {
      setErr((ex as Error).message);
    } finally {
      setCreating(false);
    }
  };



  const rowForModal =
    channelModal?.kind === 'edit' ? rows.find((r) => r.id === channelModal.integrationId) : undefined;

  const modalProvider: ProviderId | null =
    channelModal?.kind === 'new'
      ? channelModal.provider
      : rowForModal
        ? (rowForModal.provider as ProviderId)
        : null;

  const openNewChannelModal = (p: ProviderId) => {
    setErr('');
    setSaved('');
    setNewIntegrationLabel('');
    setChannelModal({ kind: 'new', provider: p });
  };

  const openEditChannelModal = (integrationId: string) => {
    setErr('');
    setSaved('');
    setChannelModal({ kind: 'edit', integrationId });
  };

  const catalog = useMemo(
    () =>
      [
        {
          id: 'facebook' as const,
          title: 'Facebook',
          subtitle: 'Messenger · página',
          accent: '#1877F2',
          logo: <LogoFacebook />,
          bullets: (
            <ul style={{ margin: '12px 0 0', paddingLeft: 18, fontSize: 12, color: '#64748b', lineHeight: 1.6 }}>
              <li>
                Producto <strong>Messenger</strong> en Meta for Developers; suscribí <code>messages</code> para la página.
              </li>
              <li>
                El <strong>Page ID</strong> debe coincidir con el webhook (<code>object: page</code>).
              </li>
            </ul>
          ),
        },
        {
          id: 'whatsapp_js' as const,
          title: 'WhatsApp · QR',
          subtitle: 'whatsapp-web.js · sesión local',
          accent: '#128C7E',
          logo: <LogoWhatsapp />,
          bullets: (
            <ul style={{ margin: '12px 0 0', paddingLeft: 18, fontSize: 12, color: '#64748b', lineHeight: 1.6 }}>
              <li>
                En Mac/Windows hace falta <strong>Google Chrome</strong> para Puppeteer (QR y vinculación por número).
              </li>
              <li>
                Sesión en disco; variable opcional <code>WWEBJS_AUTH_ROOT</code>.
              </li>
            </ul>
          ),
        },
        {
          id: 'whatsapp_meta' as const,
          title: 'WhatsApp · Meta',
          subtitle: 'Cloud API · número oficial',
          accent: '#075E54',
          logo: <LogoWhatsapp />,
          bullets: (
            <ul style={{ margin: '12px 0 0', paddingLeft: 18, fontSize: 12, color: '#64748b', lineHeight: 1.6 }}>
              <li>Mismo webhook que Messenger; suscribí mensajes de WhatsApp en la app.</li>
              <li>
                El <strong>Phone number ID</strong> debe coincidir con <code>metadata.phone_number_id</code>.
              </li>
            </ul>
          ),
        },
        {
          id: 'instagram' as const,
          title: 'Instagram',
          subtitle: 'Direct · cuenta profesional',
          accent: '#E4405F',
          logo: <LogoInstagram />,
          bullets: (
            <ul style={{ margin: '12px 0 0', paddingLeft: 18, fontSize: 12, color: '#64748b', lineHeight: 1.6 }}>
              <li>
                Suscribí <code>messages</code> para el objeto <strong>instagram</strong>.
              </li>
              <li>ID de la cuenta Instagram Business (entry del webhook), no el @usuario.</li>
            </ul>
          ),
        },
        {
          id: 'telegram' as const,
          title: 'Telegram',
          subtitle: 'Bot API · webhook',
          accent: '#229ED9',
          logo: <LogoTelegram />,
          bullets: (
            <ul style={{ margin: '12px 0 0', paddingLeft: 18, fontSize: 12, color: '#64748b', lineHeight: 1.6 }}>
              <li>
                Registrá el webhook con <code>setWebhook</code>: URL{' '}
                <code style={{ fontSize: 11 }}>/webhooks/telegram/&lt;id-integración&gt;</code> y el mismo{' '}
                <strong>secreto</strong> que cargás en el formulario.
              </li>
              <li>
                Telegram envía el secreto en la cabecera <code>X-Telegram-Bot-Api-Secret-Token</code>.
              </li>
            </ul>
          ),
        },
        {
          id: 'web_widget' as const,
          title: 'Chat web',
          subtitle: 'Script flotante · HTML para el cliente',
          accent: '#0ea5e9',
          logo: <LogoWidget />,
          bullets: (
            <ul style={{ margin: '12px 0 0', paddingLeft: 18, fontSize: 12, color: '#64748b', lineHeight: 1.6 }}>
              <li>
                Primero indicás el <strong>dominio de producción</strong> o una <strong>URL de test</strong> (localhost /
                staging); recién ahí podés <strong>generar la clave</strong>. Al <strong>guardar</strong> se persisten
                dominios y clave.
              </li>
              <li>
                El script vive en <code>/widget/embed.js</code>; el canal en consola es <code>web_widget</code>.
              </li>
            </ul>
          ),
        },
        {
          id: 'slack' as const,
          title: 'Slack',
          subtitle: 'Workspace · app bot',
          accent: '#4A154B',
          logo: <LogoSlack />,
          bullets: (
            <ul style={{ margin: '12px 0 0', paddingLeft: 18, fontSize: 12, color: '#64748b', lineHeight: 1.6 }}>
              <li>
                Guardá el <strong>Bot User OAuth Token</strong> (<code>xoxb-…</code>). Eventos entrantes requieren un
                webhook firmado (próximo paso en backend).
              </li>
            </ul>
          ),
        },
        {
          id: 'sms_twilio' as const,
          title: 'SMS · Twilio',
          subtitle: 'Número programable',
          accent: '#F22F46',
          logo: <LogoSms />,
          bullets: (
            <ul style={{ margin: '12px 0 0', paddingLeft: 18, fontSize: 12, color: '#64748b', lineHeight: 1.6 }}>
              <li>
                <strong>ID externo:</strong> número en formato E.164. <strong>Token:</strong> Auth Token de Twilio.
                Webhook de respuesta entrante aún no conectado.
              </li>
            </ul>
          ),
        },
        {
          id: 'email' as const,
          title: 'Email',
          subtitle: 'API / proveedor SMTP',
          accent: '#7c3aed',
          logo: <LogoEmail />,
          bullets: (
            <ul style={{ margin: '12px 0 0', paddingLeft: 18, fontSize: 12, color: '#64748b', lineHeight: 1.6 }}>
              <li>
                Usá el campo token para API key (SendGrid, etc.) o credencial acordada. Recepción por webhook o IMAP es
                trabajo adicional.
              </li>
            </ul>
          ),
        },
        {
          id: 'teams' as const,
          title: 'Microsoft Teams',
          subtitle: 'Azure Bot / Graph',
          accent: '#5059C9',
          logo: <LogoTeams />,
          bullets: (
            <ul style={{ margin: '12px 0 0', paddingLeft: 18, fontSize: 12, color: '#64748b', lineHeight: 1.6 }}>
              <li>
                <strong>ID externo:</strong> Microsoft App ID. <strong>Token:</strong> client secret o token de bot.
                Integración completa con Bot Framework pendiente.
              </li>
            </ul>
          ),
        },
      ].sort((a, b) => CHANNEL_ORDER.indexOf(a.id) - CHANNEL_ORDER.indexOf(b.id)),
    []
  );

  const filteredCatalog = useMemo(() => {
    const q = catalogQuery.trim().toLowerCase();
    if (!q) return catalog;
    return catalog.filter((c) => {
      const label = providerTableLabel(c.id);
      const hay = `${c.title} ${c.subtitle} ${c.id} ${label}`.toLowerCase();
      return hay.includes(q);
    });
  }, [catalog, catalogQuery]);

  return (
    <div style={{ maxWidth: 1080, margin: '0 auto', padding: '28px 24px 40px' }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16, marginBottom: 24 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 22, fontWeight: 800, letterSpacing: '-0.02em', color: '#0f172a' }}>Canales</h1>
          <p style={{ margin: '6px 0 0', fontSize: 14, color: '#64748b' }}>Conectá los canales por donde llegan tus visitantes al agente.</p>
        </div>
      </div>
      <p style={{ color: '#64748b', maxWidth: 720, marginBottom: 20, lineHeight: 1.5 }}>
        Podés tener <strong>varias integraciones del mismo tipo</strong> (varias páginas, números o sesiones QR). El
        carrusel sirve para <strong>agregar otra</strong>; la tabla lista todas (10 por página). Messenger, Instagram y
        WhatsApp Cloud comparten webhook y{' '}
        <code style={{ background: '#f1f5f9', padding: '2px 6px', borderRadius: 4 }}>META_WEBHOOK_VERIFY_TOKEN</code>.
      </p>

      <section
        style={{
          background: '#f1f5f9',
          padding: 16,
          borderRadius: 12,
          marginBottom: 24,
          fontSize: 14,
          color: '#334155',
        }}
      >
        <strong>Webhook único (Meta)</strong>
        <p style={{ margin: '8px 0 0' }}>
          Ruta:{' '}
          <code style={{ background: '#e2e8f0', padding: '4px 8px', borderRadius: 4, fontSize: 13 }}>{webhookPath}</code>{' '}
          sobre la URL pública HTTPS del backend.
        </p>
        {publicApiBase ? (
          <p style={{ margin: '10px 0 0' }}>
            Ejemplo:{' '}
            <code style={{ background: '#e2e8f0', padding: '4px 8px', borderRadius: 4, fontSize: 13 }}>
              {publicApiBase}
              {webhookPath}
            </code>
          </p>
        ) : (
          <p style={{ margin: '10px 0 0', fontSize: 13 }}>
            Opcional: <code>VITE_PUBLIC_AGENT_API_URL</code> para mostrar la URL completa.
          </p>
        )}
        <p style={{ margin: '14px 0 0', paddingTop: 12, borderTop: '1px solid #e2e8f0' }}>
          <strong>Telegram (Bot API):</strong> registrá{' '}
          <code style={{ background: '#e2e8f0', padding: '2px 6px', borderRadius: 4, fontSize: 12 }}>
            POST /webhooks/telegram/&lt;id-de-la-integración&gt;
          </code>{' '}
          con el mismo <code>secret_token</code> que guardás en el formulario.
        </p>
        <p style={{ margin: '14px 0 0', paddingTop: 12, borderTop: '1px solid #e2e8f0' }}>
          <strong>Chat web:</strong> incrustá{' '}
          <code style={{ background: '#e2e8f0', padding: '2px 6px', borderRadius: 4, fontSize: 12 }}>
            {publicApiBase ? `${publicApiBase}/widget/embed.js` : '/widget/embed.js'}
          </code>{' '}
          (CORS público solo bajo <code>/widget</code>). El código exacto aparece al editar una integración conectada.
        </p>
      </section>

      {err ? <p style={{ color: '#b91c1c', marginBottom: 12 }}>{err}</p> : null}
      {saved ? <p style={{ color: '#15803d', marginBottom: 12 }}>{saved}</p> : null}

      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          gap: 12,
          marginBottom: 12,
        }}
      >
        <h2 style={{ fontSize: 16, fontWeight: 700, color: '#0f172a', margin: 0 }}>Integrar canal</h2>
        <input
          type="search"
          placeholder="Buscar canal a integrar…"
          value={catalogQuery}
          onChange={(e) => setCatalogQuery(e.target.value)}
          aria-label="Buscar canal a integrar"
          style={{ ...inp, flex: '1 1 220px', maxWidth: 360, minWidth: 180, marginLeft: 'auto' }}
        />
      </div>
      <div
        style={{
          display: 'flex',
          gap: 16,
          overflowX: 'auto',
          scrollSnapType: 'x mandatory',
          paddingBottom: 12,
          marginBottom: 28,
          WebkitOverflowScrolling: 'touch',
        }}
      >
        {filteredCatalog.length === 0 ? (
          <p style={{ color: '#94a3b8', fontSize: 14, margin: 0, padding: '8px 0' }}>
            Ningún canal coincide con la búsqueda.
          </p>
        ) : (
          filteredCatalog.map((c) => {
          const count = rows.filter((r) => r.provider === c.id).length;
          const highlighted =
            (channelModal?.kind === 'new' && channelModal.provider === c.id) ||
            (channelModal?.kind === 'edit' && rowForModal?.provider === c.id);
          return (
            <button
              key={c.id}
              type="button"
              onClick={() => openNewChannelModal(c.id)}
              style={{
                ...carouselCardStyle(c.accent),
                outline: highlighted ? `2px solid ${c.accent}` : undefined,
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                {c.logo}
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: 16, fontWeight: 700, color: '#0f172a' }}>{c.title}</div>
                  <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>{c.subtitle}</div>
                </div>
              </div>
              <span
                style={{
                  alignSelf: 'flex-start',
                  fontSize: 11,
                  fontWeight: 700,
                  textTransform: 'uppercase',
                  letterSpacing: '0.04em',
                  padding: '4px 8px',
                  borderRadius: 6,
                  background: count > 0 ? '#dcfce7' : '#f1f5f9',
                  color: count > 0 ? '#166534' : '#64748b',
                }}
              >
                {count === 0 ? '0 integraciones' : `${count} integración${count === 1 ? '' : 'es'}`}
              </span>
              <span style={{ fontSize: 12, color: '#7c3aed', fontWeight: 600 }}>Agregar otra →</span>
            </button>
          );
        })
        )}
      </div>

      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          gap: 12,
          marginBottom: 12,
        }}
      >
        <h2 style={{ fontSize: 16, fontWeight: 700, color: '#0f172a', margin: 0 }}>Canales en tu cuenta</h2>
        <input
          type="search"
          placeholder="Buscar en tus canales…"
          value={accountQuery}
          onChange={(e) => setAccountQuery(e.target.value)}
          aria-label="Buscar en canales de tu cuenta"
          style={{ ...inp, flex: '1 1 220px', maxWidth: 360, minWidth: 180, marginLeft: 'auto' }}
        />
      </div>
      {sortedRows.length === 0 ? (
        <p style={{ color: '#94a3b8', fontSize: 14 }}>Todavía no creaste integraciones. Usá el carrusel de arriba.</p>
      ) : accountFilteredRows.length === 0 ? (
        <p style={{ color: '#94a3b8', fontSize: 14 }}>
          Ningún canal coincide con la búsqueda. Probá otro término o borrá el filtro.
        </p>
      ) : (
        <>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {tableSlice.map((row) => {
              const agName = agents.find((a) => a.id === row.virtualAgentId)?.name ?? '—';
              const busy = tableBusyId === row.id;
              const isConnected = row.status === 'connected';
              const iconBg: Record<string, string> = {
                whatsapp_js: '#dcfce7',
                whatsapp_meta: '#dcfce7',
                web_widget: '#f5f3ff',
                instagram: 'linear-gradient(135deg, #fdf497 0%, #fd5949 40%, #d6249f 70%, #285AEB 100%)',
                telegram: '#e0f2fe',
                facebook: '#ede9fe',
              };
              const bg = iconBg[row.provider] ?? '#f1f5f9';
              const isGradient = bg.startsWith('linear');
              return (
                <div
                  key={row.id}
                  style={{
                    padding: '16px 18px',
                    borderRadius: 10,
                    border: '1px solid #e2e8f0',
                    background: '#fff',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 14,
                  }}
                >
                  {/* Channel icon */}
                  <div
                    style={{
                      width: 38,
                      height: 38,
                      borderRadius: 9,
                      ...(isGradient ? { backgroundImage: bg } : { background: bg }),
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                    }}
                  >
                    {(row.provider === 'whatsapp_js' || row.provider === 'whatsapp_meta') && (
                      <svg width="20" height="20" viewBox="0 0 24 24" aria-hidden>
                        <path
                          fill="#25D366"
                          d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"
                        />
                      </svg>
                    )}
                    {row.provider === 'web_widget' && (
                      <svg width="20" height="20" viewBox="0 0 24 24" aria-hidden><rect x="3" y="6" width="18" height="12" rx="2" fill="#7c3aed" /><circle cx="9" cy="12" r="2" fill="#fff" /><circle cx="15" cy="12" r="2" fill="#fff" /></svg>
                    )}
                    {row.provider === 'instagram' && (
                      <svg width="20" height="20" viewBox="0 0 24 24" aria-hidden><rect x="2" y="2" width="20" height="20" rx="5" fill="none" /><circle cx="12" cy="12" r="4.2" fill="none" stroke="#fff" strokeWidth="1.8" /><circle cx="17.4" cy="6.6" r="1.15" fill="#fff" /></svg>
                    )}
                    {row.provider === 'telegram' && (
                      <svg width="20" height="20" viewBox="0 0 24 24" aria-hidden><circle cx="12" cy="12" r="12" fill="#229ED9" /><path fill="#fff" d="M17.2 7.3L5.8 12.1c-.7.3-.7.7-.1.9l2.9.9 6.7-4.2c.4-.2.7-.1.4.1l-5.4 4.9-.2 3c.2 0 .3-.1.5-.2l2.4-2.3 5 3.7c.9.5 1.6.2 1.8-.7l1.8-8.5c.3-1.2-.5-1.7-1.3-1.4z"/></svg>
                    )}
                    {row.provider === 'facebook' && (
                      <svg width="20" height="20" viewBox="0 0 24 24" aria-hidden><path fill="#1877F2" d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
                    )}
                    {row.provider === 'email' && (
                      <svg width="20" height="20" viewBox="0 0 24 24" aria-hidden><rect x="2" y="4" width="20" height="16" rx="2" fill="#7c3aed" /><path fill="#ede9fe" d="M2 6l10 7 10-7v-2H2v2z" /></svg>
                    )}
                    {row.provider === 'sms_twilio' && (
                      <svg width="20" height="20" viewBox="0 0 24 24" aria-hidden><rect x="3" y="5" width="18" height="14" rx="2" fill="#F22F46" /><path fill="#fff" d="M7 9h10v2H7V9zm0 3h7v2H7v-2z" /></svg>
                    )}
                    {row.provider === 'slack' && (
                      <svg width="20" height="20" viewBox="0 0 24 24" aria-hidden><path fill="#E01E5A" d="M5.2 15.2a2.4 2.4 0 0 1-2.4 2.4 2.4 2.4 0 1 1 2.4-2.4zM8 15.2h2.4v2.4a2.4 2.4 0 1 1-2.4-2.4V15.2z" /><path fill="#36C5F0" d="M8.8 5.2a2.4 2.4 0 1 1 4.8 0v2.4H11.2a2.4 2.4 0 1 1-2.4-2.4V5.2z" /><path fill="#2EB67D" d="M18.8 8.8a2.4 2.4 0 0 1 2.4 2.4 2.4 2.4 0 1 1-2.4-2.4H18.8zm-2.4 4.8V11.2h2.4a2.4 2.4 0 1 1-2.4 2.4z" /><path fill="#ECB22E" d="M15.2 18.8a2.4 2.4 0 1 1-4.8 0v-2.4h2.4a2.4 2.4 0 1 1 2.4 2.4v0z" /></svg>
                    )}
                    {row.provider === 'teams' && (
                      <svg width="20" height="20" viewBox="0 0 24 24" aria-hidden><rect x="2" y="3" width="9" height="9" rx="1" fill="#5059C9" /><rect x="13" y="3" width="9" height="9" rx="1" fill="#7B83EB" /><rect x="2" y="14" width="9" height="7" rx="1" fill="#5059C9" /><rect x="13" y="14" width="9" height="7" rx="1" fill="#7B83EB" /></svg>
                    )}
                  </div>
                  {/* Info */}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 14, fontWeight: 700, color: '#0f172a' }}>
                      {row.displayName || providerTableLabel(row.provider)}
                    </div>
                    <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 2 }}>
                      {providerTableLabel(row.provider)} · {isConnected ? <span style={{ color: '#16a34a', fontWeight: 600 }}>Conectado</span> : <span style={{ color: '#94a3b8' }}>Desconectado</span>}
                      {agName !== '—' && <> · <span style={{ color: '#64748b' }}>{agName}</span></>}
                    </div>
                  </div>
                  {/* Action button */}
                  <button
                    type="button"
                    disabled={busy}
                    onClick={() => openEditChannelModal(row.id)}
                    style={{ padding: '6px 11px', borderRadius: 6, border: '1px solid #e2e8f0', background: '#fff', color: '#334155', fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}
                  >
                    Configurar
                  </button>
                </div>
              );
            })}
          </div>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginTop: 12,
              fontSize: 13,
              color: '#64748b',
            }}
          >
            <span>
              Página {tablePage} de {tableTotalPages} · {accountFilteredRows.length} canal(es)
              {accountQuery.trim() ? ` (de ${sortedRows.length} en total)` : ''}
            </span>
            <div style={{ display: 'flex', gap: 8 }}>
              <button
                type="button"
                disabled={tablePage <= 1}
                style={btnTable}
                onClick={() => setTablePage((p) => Math.max(1, p - 1))}
              >
                Anterior
              </button>
              <button
                type="button"
                disabled={tablePage >= tableTotalPages}
                style={btnTable}
                onClick={() => setTablePage((p) => Math.min(tableTotalPages, p + 1))}
              >
                Siguiente
              </button>
            </div>
          </div>
        </>
      )}

      {channelModal && modalProvider ? (
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="va-channel-modal-title"
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(15, 23, 42, 0.45)',
            zIndex: 1000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 20,
            boxSizing: 'border-box',
          }}
          onClick={() => setChannelModal(null)}
        >
          <div
            style={{
              background: '#fff',
              borderRadius: 16,
              maxWidth: 520,
              width: '100%',
              maxHeight: 'min(92vh, 900px)',
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
              boxShadow: '0 24px 48px rgba(15, 23, 42, 0.2)',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div
              style={{
                padding: '16px 20px',
                borderBottom: '1px solid #f1f5f9',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 12,
              }}
            >
              <h2 id="va-channel-modal-title" style={{ margin: 0, fontSize: 18, fontWeight: 700, color: '#0f172a' }}>
                {catalog.find((x) => x.id === modalProvider)?.title ?? modalProvider}
                {channelModal.kind === 'edit' && rowForModal?.displayName ? (
                  <span style={{ fontWeight: 600, color: '#64748b', fontSize: 15 }}> · {rowForModal.displayName}</span>
                ) : null}
              </h2>
              <button
                type="button"
                aria-label="Cerrar"
                onClick={() => setChannelModal(null)}
                style={{
                  border: 'none',
                  background: '#f1f5f9',
                  borderRadius: 8,
                  width: 36,
                  height: 36,
                  cursor: 'pointer',
                  fontSize: 18,
                  lineHeight: 1,
                  color: '#475569',
                }}
              >
                ×
              </button>
            </div>
            <div style={{ padding: 20, overflowY: 'auto', flex: 1, minHeight: 0 }}>
              {catalog.find((x) => x.id === modalProvider)?.bullets}
              {channelModal.kind === 'new' ? (
                <div style={{ marginTop: 20 }}>
                  <p style={{ fontSize: 14, color: '#475569', marginTop: 0 }}>
                    Se creará una <strong>nueva</strong> fila en la tabla, asignada al agente seleccionado
                    {selectedAgent ? (
                      <>
                        : <strong>{selectedAgent.name}</strong>
                      </>
                    ) : (
                      <> (elegí uno arriba).</>
                    )}
                    . Podés repetir el mismo tipo de canal varias veces.
                  </p>
                  {!selectedAgentId ? (
                    <p style={{ fontSize: 13, color: '#b91c1c', marginTop: 0 }} role="alert">
                      Seleccioná un agente en el selector superior para continuar.
                    </p>
                  ) : null}
                  <label style={{ ...lab, marginTop: 12 }}>
                    Nombre interno (opcional)
                    <input
                      style={inp}
                      value={newIntegrationLabel}
                      onChange={(e) => setNewIntegrationLabel(e.target.value)}
                      placeholder="Ej. Fanpage principal, WhatsApp ventas…"
                    />
                  </label>
                  <button
                    type="button"
                    style={channelCreateButtonStyle(modalProvider)}
                    disabled={!selectedAgentId || creating}
                    onClick={() => void createIntegration(modalProvider)}
                  >
                    {creating ? 'Creando…' : 'Crear integración'}
                  </button>
                </div>
              ) : rowForModal ? (
                modalProvider === 'whatsapp_js' ? (
                  <div style={{ marginTop: 16 }}>
                    <WhatsappJsPanel
                      row={rowForModal}
                      agents={agents}
                      token={token}
                      onSaved={() => void load()}
                      setErr={setErr}
                      setSaved={setSaved}
                    />
                  </div>
                ) : modalProvider === 'facebook' ? (
                  <div style={{ marginTop: 16 }}>
                    <ChannelConfigForm
                      row={rowForModal}
                      agents={agents}
                      token={token}
                      onSaved={() => void load()}
                      setErr={setErr}
                      setSaved={setSaved}
                      externalIdLabel="Page ID"
                      externalIdPlaceholder="ID numérico de la página"
                      tokenFieldLabel="Page Access Token"
                      tokenHint="Token de página con permisos de mensajes."
                      submitLabel="Guardar Messenger"
                    />
                  </div>
                ) : modalProvider === 'whatsapp_meta' ? (
                  <div style={{ marginTop: 16 }}>
                    <ChannelConfigForm
                      row={rowForModal}
                      agents={agents}
                      token={token}
                      onSaved={() => void load()}
                      setErr={setErr}
                      setSaved={setSaved}
                      externalIdLabel="Phone number ID"
                      externalIdPlaceholder="Desde Meta for Developers → WhatsApp → API Setup"
                      tokenFieldLabel="Access token (Cloud API)"
                      tokenHint="Token permanente o temporal con whatsapp_business_messaging, etc."
                      submitLabel="Guardar WhatsApp (Meta)"
                    />
                  </div>
                ) : modalProvider === 'instagram' ? (
                  <div style={{ marginTop: 16 }}>
                    <ChannelConfigForm
                      row={rowForModal}
                      agents={agents}
                      token={token}
                      onSaved={() => void load()}
                      setErr={setErr}
                      setSaved={setSaved}
                      externalIdLabel="Instagram Business Account ID"
                      externalIdPlaceholder="ig-user-id (Graph API Explorer)"
                      tokenFieldLabel="Access Token"
                      tokenHint="Token con instagram_manage_messages (cuenta vinculada a la app)."
                      submitLabel="Guardar Instagram"
                    />
                  </div>
                ) : modalProvider === 'telegram' ? (
                  <div style={{ marginTop: 16 }}>
                    <ChannelConfigForm
                      row={rowForModal}
                      agents={agents}
                      token={token}
                      onSaved={() => void load()}
                      setErr={setErr}
                      setSaved={setSaved}
                      externalIdLabel="Secreto webhook (secret_token)"
                      externalIdPlaceholder="Mismo valor que en setWebhook — cabecera X-Telegram-Bot-Api-Secret-Token"
                      tokenFieldLabel="Token del bot"
                      tokenHint="De @BotFather, formato 123456:ABC-DEF..."
                      submitLabel="Guardar Telegram"
                    />
                  </div>
                ) : modalProvider === 'web_widget' ? (
                  <div style={{ marginTop: 16 }}>
                    <WebChatIntegrationForm
                      row={rowForModal}
                      agents={agents}
                      token={token}
                      onSaved={() => void load()}
                      setErr={setErr}
                      setSaved={setSaved}
                    />
                  </div>
                ) : modalProvider === 'slack' ? (
                  <div style={{ marginTop: 16 }}>
                    <ChannelConfigForm
                      row={rowForModal}
                      agents={agents}
                      token={token}
                      onSaved={() => void load()}
                      setErr={setErr}
                      setSaved={setSaved}
                      externalIdLabel="Signing secret (opcional)"
                      externalIdPlaceholder="Para verificar eventos Slack"
                      tokenFieldLabel="Bot User OAuth Token"
                      tokenHint="xoxb-… desde OAuth & Permissions de la app."
                      submitLabel="Guardar Slack"
                    />
                  </div>
                ) : modalProvider === 'sms_twilio' ? (
                  <div style={{ marginTop: 16 }}>
                    <ChannelConfigForm
                      row={rowForModal}
                      agents={agents}
                      token={token}
                      onSaved={() => void load()}
                      setErr={setErr}
                      setSaved={setSaved}
                      externalIdLabel="Número Twilio (E.164)"
                      externalIdPlaceholder="+59899123456"
                      tokenFieldLabel="Auth Token"
                      tokenHint="Panel Twilio → cuenta → Auth Token del número."
                      submitLabel="Guardar Twilio"
                    />
                  </div>
                ) : modalProvider === 'email' ? (
                  <div style={{ marginTop: 16 }}>
                    <ChannelConfigForm
                      row={rowForModal}
                      agents={agents}
                      token={token}
                      onSaved={() => void load()}
                      setErr={setErr}
                      setSaved={setSaved}
                      externalIdLabel="Identificador / remitente (opcional)"
                      externalIdPlaceholder="soporte@tudominio.com o ID de buzón"
                      tokenFieldLabel="API key o credencial"
                      tokenHint="SendGrid, Postmark, SMTP acordado, etc."
                      submitLabel="Guardar Email"
                    />
                  </div>
                ) : modalProvider === 'teams' ? (
                  <div style={{ marginTop: 16 }}>
                    <ChannelConfigForm
                      row={rowForModal}
                      agents={agents}
                      token={token}
                      onSaved={() => void load()}
                      setErr={setErr}
                      setSaved={setSaved}
                      externalIdLabel="Microsoft App (client) ID"
                      externalIdPlaceholder="UUID de la app en Azure"
                      tokenFieldLabel="Client secret / token de bot"
                      tokenHint="Azure Portal → certificados y secretos o token de Bot Framework."
                      submitLabel="Guardar Teams"
                    />
                  </div>
                ) : (
                  <div style={{ marginTop: 16 }}>
                    <ChannelConfigForm
                      row={rowForModal}
                      agents={agents}
                      token={token}
                      onSaved={() => void load()}
                      setErr={setErr}
                      setSaved={setSaved}
                      externalIdLabel="ID / clave externa"
                      externalIdPlaceholder="—"
                      tokenFieldLabel="Token o API key"
                      tokenHint="Completá según el proveedor del canal."
                      submitLabel="Guardar"
                    />
                  </div>
                )
              ) : (
                <p style={{ color: '#94a3b8', marginTop: 16 }}>Cargando integración…</p>
              )}
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

function ChannelConfigForm({
  row,
  agents,
  token,
  onSaved,
  setErr,
  setSaved,
  externalIdLabel,
  externalIdPlaceholder,
  tokenFieldLabel,
  tokenHint,
  submitLabel,
}: {
  row: Integ;
  agents: Agent[];
  token: string;
  onSaved: () => void;
  setErr: (s: string) => void;
  setSaved: (s: string) => void;
  externalIdLabel: string;
  externalIdPlaceholder: string;
  tokenFieldLabel: string;
  tokenHint: string;
  submitLabel: string;
}) {
  const [externalId, setExternalId] = useState(row.externalPageId ?? '');
  const [agentId, setAgentId] = useState(row.virtualAgentId ?? '');
  const [accessTok, setAccessTok] = useState('');
  const [status, setStatus] = useState(row.status);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setExternalId(row.externalPageId ?? '');
    setAgentId(row.virtualAgentId ?? '');
    setStatus(row.status);
    setAccessTok('');
  }, [row.id, row.externalPageId, row.virtualAgentId, row.status, row.hasPageToken]);

  const save = async (e: React.FormEvent) => {
    e.preventDefault();
    setErr('');
    setSaved('');
    setBusy(true);
    try {
      const body: Record<string, unknown> = {
        externalPageId: externalId.trim() || null,
        virtualAgentId: agentId.trim() || null,
        status,
      };
      if (accessTok.trim()) body.accessToken = accessTok.trim();
      await apiPatch(`/api/console/integrations/${row.id}`, body, token, { successMessage: 'Cambios guardados' });
      setSaved('Guardado.');
      setAccessTok('');
      onSaved();
    } catch (ex) {
      setErr((ex as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={(e) => void save(e)}>
      <div style={{ display: 'grid', gap: 12 }}>
        <label style={lab}>
          {externalIdLabel}
          <input
            style={inp}
            value={externalId}
            onChange={(e) => setExternalId(e.target.value)}
            placeholder={externalIdPlaceholder}
          />
        </label>
        <label style={lab}>
          Agente virtual
          <select style={inp} value={agentId} onChange={(e) => setAgentId(e.target.value)}>
            <option value="">— Elegir —</option>
            {agents.map((a) => (
              <option key={a.id} value={a.id}>
                {a.name}
              </option>
            ))}
          </select>
        </label>
        <label style={lab}>
          {tokenFieldLabel}
          {row.hasPageToken ? (
            <span style={{ fontWeight: 400, color: '#64748b' }}> · hay token guardado</span>
          ) : null}
          <input
            style={inp}
            type="password"
            autoComplete="off"
            value={accessTok}
            onChange={(e) => setAccessTok(e.target.value)}
            placeholder={row.hasPageToken ? 'Vacío = no cambiar' : 'Pegar token'}
          />
          <span style={{ fontWeight: 400, fontSize: 11, color: '#94a3b8' }}>{tokenHint}</span>
        </label>
        <label style={lab}>
          Estado
          <select style={inp} value={status} onChange={(e) => setStatus(e.target.value)}>
            <option value="disconnected">Desconectado</option>
            <option value="connected">Conectado</option>
          </select>
        </label>
        <button type="submit" disabled={busy} style={btnPrimary}>
          {busy ? 'Guardando…' : submitLabel}
        </button>
      </div>
    </form>
  );
}

const lab: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  fontSize: 13,
  fontWeight: 600,
  color: '#475569',
  gap: 4,
};
const inp: React.CSSProperties = {
  padding: '10px 12px',
  borderRadius: 8,
  border: '1px solid #cbd5e1',
  fontSize: 14,
};
const btnPrimary: React.CSSProperties = {
  padding: '10px 16px',
  borderRadius: 8,
  border: 'none',
  background: '#1877F2',
  color: '#fff',
  fontWeight: 600,
  cursor: 'pointer',
};
const btnTable: React.CSSProperties = {
  padding: '6px 12px',
  borderRadius: 8,
  border: '1px solid #cbd5e1',
  background: '#fff',
  color: '#334155',
  fontWeight: 600,
  fontSize: 12,
  cursor: 'pointer',
};

const btnInstagram: React.CSSProperties = {
  ...btnPrimary,
  background: 'linear-gradient(45deg, #f09433 0%, #e6683c 25%, #dc2743 50%, #cc2366 75%, #bc1888 100%)',
};
const btnWhatsappJs: React.CSSProperties = {
  ...btnPrimary,
  background: '#128C7E',
};
const btnWhatsappMeta: React.CSSProperties = {
  ...btnPrimary,
  background: '#075E54',
};
const btnTelegram: React.CSSProperties = { ...btnPrimary, background: '#229ED9' };
const btnSlack: React.CSSProperties = { ...btnPrimary, background: '#4A154B' };
const btnEmailCh: React.CSSProperties = { ...btnPrimary, background: '#7c3aed' };
const btnTwilio: React.CSSProperties = { ...btnPrimary, background: '#F22F46' };
const btnTeams: React.CSSProperties = { ...btnPrimary, background: '#5059C9' };
const btnWidget: React.CSSProperties = { ...btnPrimary, background: '#0ea5e9' };

function channelCreateButtonStyle(p: ProviderId): React.CSSProperties {
  switch (p) {
    case 'instagram':
      return btnInstagram;
    case 'whatsapp_js':
      return btnWhatsappJs;
    case 'whatsapp_meta':
      return btnWhatsappMeta;
    case 'telegram':
      return btnTelegram;
    case 'slack':
      return btnSlack;
    case 'email':
      return btnEmailCh;
    case 'sms_twilio':
      return btnTwilio;
    case 'teams':
      return btnTeams;
    case 'web_widget':
      return btnWidget;
    default:
      return btnPrimary;
  }
}

function readWhatsappJsPolicy(row: Integ): { mode: 'none' | 'all' | 'allowlist'; allowlistText: string } {
  const wj = row.providerSettings?.whatsapp_js;
  const m = wj?.inboundMode;
  const mode: 'none' | 'all' | 'allowlist' =
    m === 'all' || m === 'allowlist' || m === 'none' ? m : 'none';
  const allowlistText = (wj?.allowlist ?? []).join('\n');
  return { mode, allowlistText };
}

function WhatsappJsPanel({
  row,
  agents,
  token,
  onSaved,
  setErr,
  setSaved,
}: {
  row: Integ;
  agents: Agent[];
  token: string;
  onSaved: () => void;
  setErr: (s: string) => void;
  setSaved: (s: string) => void;
}) {
  const [agentId, setAgentId] = useState(row.virtualAgentId ?? '');
  const [pairingPhoneInput, setPairingPhoneInput] = useState('');
  const [runtime, setRuntime] = useState<WhatsappJsRuntime | null>(null);
  const [busy, setBusy] = useState(false);
  const [inboundMode, setInboundMode] = useState<'none' | 'all' | 'allowlist'>(() => readWhatsappJsPolicy(row).mode);
  const [allowlistText, setAllowlistText] = useState(() => readWhatsappJsPolicy(row).allowlistText);
  const [policyBusy, setPolicyBusy] = useState(false);

  useEffect(() => {
    setAgentId(row.virtualAgentId ?? '');
  }, [row.virtualAgentId, row.id]);

  useEffect(() => {
    const p = readWhatsappJsPolicy(row);
    setInboundMode(p.mode);
    setAllowlistText(p.allowlistText);
  }, [row.id, row.providerSettings]);

  const poll = useCallback(async () => {
    try {
      const data = await apiGet<WhatsappJsRuntime>(`/api/console/integrations/${row.id}/whatsapp-js/status`, token);
      setRuntime(data);
    } catch {
      /* ignorar durante desmontaje */
    }
  }, [row.id, token]);

  useEffect(() => {
    void poll();
    const id = window.setInterval(() => void poll(), 900);
    return () => window.clearInterval(id);
  }, [poll]);

  const saveAgent = async (e: React.FormEvent) => {
    e.preventDefault();
    setErr('');
    setSaved('');
    setBusy(true);
    try {
      await apiPatch(
        `/api/console/integrations/${row.id}`,
        { virtualAgentId: agentId.trim() || null },
        token,
        { successMessage: 'Agente guardado' }
      );
      setSaved('Agente guardado.');
      onSaved();
    } catch (ex) {
      setErr((ex as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const saveInboundPolicy = async (e: React.FormEvent) => {
    e.preventDefault();
    setErr('');
    setSaved('');
    setPolicyBusy(true);
    try {
      const lines = allowlistText
        .split(/[\n,;]+/)
        .map((s) => s.trim())
        .filter(Boolean);
      await apiPatch(
        `/api/console/integrations/${row.id}`,
        {
          providerSettings: {
            whatsapp_js: {
              inboundMode,
              allowlist: inboundMode === 'allowlist' ? lines : [],
            },
          },
        },
        token,
        { successMessage: 'Reglas de mensajes entrantes guardadas' }
      );
      setSaved('Reglas de mensajes entrantes guardadas.');
      onSaved();
    } catch (ex) {
      setErr((ex as Error).message);
    } finally {
      setPolicyBusy(false);
    }
  };

  const startClient = async () => {
    setErr('');
    setSaved('');
    const chosen = agentId.trim();
    if (!chosen) {
      setErr('Elegí un agente virtual en la lista. Si no hay ninguno, creá uno en Agentes.');
      return;
    }
    setBusy(true);
    try {
      if (!row.virtualAgentId || row.virtualAgentId !== chosen) {
        await apiPatch(`/api/console/integrations/${row.id}`, { virtualAgentId: chosen }, token);
        onSaved();
      }
      const digits = pairingPhoneInput.replace(/\D/g, '');
      const startBody =
        digits.length >= 10 ? { pairingPhone: pairingPhoneInput.trim() } : {};
      await apiPost(`/api/console/integrations/${row.id}/whatsapp-js/start`, startBody, token, {
        successMessage: 'Cliente WhatsApp iniciado',
      });
      setSaved(
        digits.length >= 10
          ? 'Pedimos el código de vinculación a tu número. Debería aparecer abajo en unos segundos.'
          : 'Cliente iniciado. Si aparece QR, escanealo con WhatsApp → Dispositivos vinculados.'
      );
      await poll();
      onSaved();
    } catch (ex) {
      setErr((ex as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const logout = async () => {
    setErr('');
    setSaved('');
    setBusy(true);
    setRuntime(null);
    try {
      await apiPost(
        `/api/console/integrations/${row.id}/whatsapp-js/logout`,
        {},
        token,
        {
          timeoutMs: 90_000,
          successMessage: 'Sesión de WhatsApp cerrada',
        }
      );
      setSaved('Sesión cerrada y datos locales borrados.');
      onSaved();
      await poll();
    } catch (ex) {
      setErr((ex as Error).message);
      await poll();
    } finally {
      setBusy(false);
    }
  };

  const phase = runtime?.phase ?? 'off';
  const canStart = agents.length > 0;
  const agentMissing = !agentId.trim();
  const showQrEmbed =
    phase === 'starting' || phase === 'qr' || (phase === 'error' && Boolean(runtime?.error));

  return (
    <div style={{ display: 'grid', gap: 14 }}>
      <style
        dangerouslySetInnerHTML={{
          __html: '@keyframes vaQrSpin{to{transform:rotate(360deg)}}',
        }}
      />
      {agents.length === 0 ? (
        <div
          style={{
            background: '#fff7ed',
            border: '1px solid #fdba74',
            borderRadius: 10,
            padding: 12,
            fontSize: 14,
            color: '#9a3412',
            lineHeight: 1.45,
          }}
        >
          No tenés agentes virtuales.{' '}
          <Link to="/app/agents" style={{ color: '#c2410c', fontWeight: 700 }}>
            Crear agente
          </Link>{' '}
          y volvé acá para vincular WhatsApp.
        </div>
      ) : (
        <div
          style={{
            background: '#eff6ff',
            border: '1px solid #93c5fd',
            borderRadius: 10,
            padding: 12,
            fontSize: 14,
            color: '#1e3a8a',
            lineHeight: 1.45,
          }}
        >
          <strong>Paso 1:</strong> elegí el agente. <strong>Paso 2:</strong> opcional, número con código de país para
          vincular <em>sin QR</em>. <strong>Paso 3:</strong> <strong>Iniciar cliente</strong> (guarda el agente si hace
          falta).
        </div>
      )}

      <form
        onSubmit={(e) => void saveInboundPolicy(e)}
        style={{
          background: '#f8fafc',
          border: '1px solid #e2e8f0',
          borderRadius: 10,
          padding: 14,
          display: 'grid',
          gap: 10,
        }}
      >
        <div style={{ fontSize: 14, fontWeight: 700, color: '#0f172a' }}>Mensajes entrantes (bot automático)</div>
        <p style={{ margin: 0, fontSize: 13, color: '#64748b', lineHeight: 1.5 }}>
          Por defecto <strong>el bot no interactúa con ningún número</strong>. Sólo si elegís <em>Todos</em> o una{' '}
          <em>lista permitida</em> se creará conversación y se aplicará el agente. La sesión de WhatsApp puede seguir
          conectada; simplemente se ignoran los chats no permitidos.
        </p>
        <div style={{ display: 'grid', gap: 8 }}>
          <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, fontSize: 14, color: '#334155', cursor: 'pointer' }}>
            <input
              type="radio"
              name="inbound"
              checked={inboundMode === 'none'}
              onChange={() => setInboundMode('none')}
              disabled={policyBusy || busy}
            />
            <span>
              <strong>Ninguno</strong> (por defecto) — el bot no atiende chats entrantes
            </span>
          </label>
          <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, fontSize: 14, color: '#334155', cursor: 'pointer' }}>
            <input
              type="radio"
              name="inbound"
              checked={inboundMode === 'all'}
              onChange={() => setInboundMode('all')}
              disabled={policyBusy || busy}
            />
            <span>
              <strong>Todos</strong> — cualquier contacto que escriba a este WhatsApp
            </span>
          </label>
          <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, fontSize: 14, color: '#334155', cursor: 'pointer' }}>
            <input
              type="radio"
              name="inbound"
              checked={inboundMode === 'allowlist'}
              onChange={() => setInboundMode('allowlist')}
              disabled={policyBusy || busy}
            />
            <span>
              <strong>Sólo estos números</strong> (mismo formato que en WhatsApp: con código de país, sólo dígitos)
            </span>
          </label>
        </div>
        {inboundMode === 'allowlist' ? (
          <label style={lab}>
            Números permitidos
            <textarea
              style={{ ...inp, minHeight: 88, fontFamily: 'ui-monospace, monospace', fontSize: 13 }}
              value={allowlistText}
              onChange={(e) => setAllowlistText(e.target.value)}
              placeholder={'59899123456\n5491122334455'}
              disabled={policyBusy || busy}
            />
            <span style={{ fontWeight: 400, fontSize: 11, color: '#94a3b8' }}>Uno por línea. Podés escribir con o sin +.</span>
          </label>
        ) : null}
        <button
          type="submit"
          disabled={policyBusy || busy}
          style={{ ...btnPrimary, justifySelf: 'start', background: '#475569' }}
        >
          {policyBusy ? '…' : 'Guardar reglas de inbound'}
        </button>
      </form>

      <form onSubmit={(e) => void saveAgent(e)}>
        <label style={lab}>
          Agente virtual
          <select style={inp} value={agentId} onChange={(e) => setAgentId(e.target.value)} disabled={busy}>
            <option value="">— Elegir —</option>
            {agents.map((a) => (
              <option key={a.id} value={a.id}>
                {a.name}
              </option>
            ))}
          </select>
        </label>
        <button type="submit" disabled={busy} style={{ ...btnPrimary, marginTop: 10, background: '#128C7E' }}>
          {busy ? '…' : 'Guardar agente'}
        </button>
      </form>

      <label style={lab}>
        Número para vincular sin QR (opcional)
        <input
          style={inp}
          type="tel"
          autoComplete="tel"
          placeholder="Ej. +59899123456 (solo dígitos, con código de país)"
          value={pairingPhoneInput}
          onChange={(e) => setPairingPhoneInput(e.target.value)}
          disabled={busy}
        />
        <span style={{ fontWeight: 400, fontSize: 11, color: '#94a3b8' }}>
          Si completás esto y pulsás Iniciar, WhatsApp pedirá un código de 8 letras en el teléfono en lugar del QR.
        </span>
      </label>

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
        <button
          type="button"
          disabled={busy || !canStart || agentMissing}
          style={{
            ...btnWhatsappJs,
            opacity: busy || !canStart || agentMissing ? 0.55 : 1,
            cursor: busy || !canStart || agentMissing ? 'not-allowed' : 'pointer',
          }}
          title={
            agentMissing ? 'Elegí un agente en la lista' : !canStart ? 'Creá un agente primero' : undefined
          }
          onClick={() => void startClient()}
        >
          Iniciar cliente{pairingPhoneInput.replace(/\D/g, '').length >= 10 ? ' (código)' : ' (QR)'}
        </button>
        <button type="button" disabled={busy} style={btnDanger} onClick={() => void logout()}>
          {busy ? 'Cerrando…' : 'Cerrar sesión WhatsApp'}
        </button>
      </div>

      <p style={{ margin: 0, fontSize: 13, color: '#475569' }}>
        Estado BD: <strong>{runtime?.dbStatus ?? row.status}</strong>
        {runtime?.externalPageId ? (
          <>
            {' '}
            · Número: <code>{runtime.externalPageId}</code>
          </>
        ) : null}
        {runtime?.displayName ? (
          <>
            {' '}
            · {runtime.displayName}
          </>
        ) : null}
        <br />
        Cliente en memoria: <strong>{phase}</strong>
        {typeof runtime?.loadingPercent === 'number' ? (
          <>
            {' '}
            · Carga: <strong>{runtime.loadingPercent}%</strong>
          </>
        ) : null}
        {runtime?.error ? (
          <>
            <br />
            <span style={{ color: '#b91c1c' }}>{runtime.error}</span>
          </>
        ) : null}
      </p>

      {showQrEmbed ? (
        <div
          style={{
            border: '1px solid #e2e8f0',
            borderRadius: 16,
            background: '#f8fafc',
            padding: 24,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 14,
            minHeight: 340,
          }}
        >
          {phase === 'error' && runtime?.error ? (
            <p style={{ margin: 0, fontSize: 14, color: '#b91c1c', textAlign: 'center', maxWidth: 420, lineHeight: 1.5 }}>
              {runtime.error}
            </p>
          ) : runtime?.pairingCode ? (
            <>
              <p style={{ margin: 0, fontSize: 16, fontWeight: 700, color: '#0f172a' }}>Código de vinculación</p>
              <p
                style={{
                  margin: 0,
                  fontSize: 40,
                  fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                  fontWeight: 800,
                  letterSpacing: 8,
                  color: '#128C7E',
                }}
              >
                {runtime.pairingCode}
              </p>
              <p style={{ margin: 0, fontSize: 13, color: '#64748b', textAlign: 'center', maxWidth: 380, lineHeight: 1.5 }}>
                En el teléfono: <strong>Dispositivos vinculados</strong> → <strong>Vincular con número</strong> → ingresá
                este código cuando WhatsApp lo pida.
              </p>
            </>
          ) : runtime?.qrDataUrl ? (
            <>
              <p style={{ margin: 0, fontSize: 16, fontWeight: 700, color: '#0f172a' }}>Escanear código QR</p>
              <p style={{ margin: 0, fontSize: 13, color: '#64748b', textAlign: 'center', maxWidth: 340 }}>
                WhatsApp en el teléfono → Menú (⋮) → Dispositivos vinculados → Vincular dispositivo
              </p>
              <img
                src={runtime.qrDataUrl}
                alt="Código QR para vincular WhatsApp"
                width={280}
                height={280}
                style={{
                  display: 'block',
                  borderRadius: 12,
                  background: '#fff',
                  padding: 12,
                  boxShadow: '0 4px 20px rgba(15, 23, 42, 0.1)',
                }}
              />
            </>
          ) : phase === 'qr' && runtime?.hasQrPayload ? (
            <p style={{ margin: 0, fontSize: 14, color: '#b45309', textAlign: 'center', maxWidth: 360 }}>
              El código llegó pero no se pudo mostrar como imagen. Probá cerrar sesión y volver a iniciar, o revisá el
              log del servidor.
            </p>
          ) : (
            <>
              <div
                aria-hidden
                style={{
                  width: 48,
                  height: 48,
                  border: '4px solid #e2e8f0',
                  borderTopColor: '#128C7E',
                  borderRadius: '50%',
                  animation: 'vaQrSpin 0.85s linear infinite',
                }}
              />
              <p style={{ margin: 0, fontSize: 15, fontWeight: 600, color: '#334155' }}>
                {runtime?.linkMode === 'phone' ? 'Solicitando código de vinculación…' : 'Generando código QR…'}
              </p>
              {typeof runtime?.loadingPercent === 'number' ? (
                <p style={{ margin: 0, fontSize: 13, color: '#64748b' }}>
                  Cargando WhatsApp Web: {runtime.loadingPercent}%
                </p>
              ) : (
                <p style={{ margin: 0, fontSize: 12, color: '#94a3b8', textAlign: 'center', maxWidth: 380 }}>
                  {runtime?.linkMode === 'phone'
                    ? 'El código de 8 caracteres aparecerá aquí. En el teléfono seguí el flujo de “vincular dispositivo”.'
                    : 'El QR aparecerá aquí cuando WhatsApp Web lo entregue. En Mac el backend intenta usar Google Chrome instalado; si no tenés Chrome, instalalo y reiniciá el servidor.'}
                </p>
              )}
            </>
          )}
        </div>
      ) : null}
    </div>
  );
}

const btnDanger: React.CSSProperties = {
  padding: '10px 16px',
  borderRadius: 8,
  border: '1px solid #cbd5e1',
  background: '#fff',
  color: '#b91c1c',
  fontWeight: 600,
  cursor: 'pointer',
};
